import { CheckCircleFilled, ClockCircleOutlined, WarningFilled } from '@ant-design/icons'
import { defaultNumberDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { Tooltip } from 'antd'
import React from 'react'

const statusRenderer = ({ data }) => {
  const isProjected = data?.is_projected
  const hasErrors = data?.has_errors
  if (isProjected) {
    if (hasErrors) {
      return (
        <Tooltip title='Projected With Errors'>
          <WarningFilled style={{ color: 'var(--theme-warning)' }} />
        </Tooltip>
      )
    }
    return (
      <Tooltip title='Projected'>
        <ClockCircleOutlined />
      </Tooltip>
    )
  }
  if (hasErrors) {
    return (
      <Tooltip title='Actual With Errors'>
        <WarningFilled style={{ color: 'var(--theme-error)' }} />
      </Tooltip>
    )
  }
  return (
    <Tooltip title='Actual'>
      <CheckCircleFilled style={{ color: 'var(--theme-success)' }} />
    </Tooltip>
  )
}

const statusGetter = ({ data }) => {
  const isProjected = data?.is_projected
  const hasErrors = data?.has_errors
  if (isProjected) {
    if (hasErrors) {
      return 'Projected With Errors'
    }
    return 'Projected'
  }
  if (hasErrors) {
    return 'Actual With Errors'
  }
  return 'Actual'
}

export const getColumnDefs = (selectors, formattedCurrency) => {
  return [
    {
      headerName: 'COST IDENTIFIER',
      field: 'inventory_costing_identifier',
      groupId: 'cost_identifier',
      children: [
        {
          field: 'is_projected',
          headerName: 'Status',
          cellRenderer: statusRenderer,
          valueGetter: statusGetter,
        },
        ...selectors?.map((selector) => ({
          field: selector,
          valueGetter: ({ data }) => data?.inventory_costing_identifier?.[selector],
          enableRowGroup: true,
          comparator: (a, b) => {
            if (selector === 'site') {
              return a - b
            }
            if (selector === 'product') {
              const orderBool = a > b
              return orderBool ? 1 : -1
            }
          },
        })),
      ],
    },
    {
      field: 'initial_vpv',
      headerName: 'INITIAL',
      groupId: 'initial',
      children: [
        {
          field: 'initial_vpv.volume',
          headerName: 'Volume',
          enableRowGroup: false,
          ...defaultNumberDef('initial_vpv', 0, null, 'volume'),
        },
        {
          field: 'initial_vpv.value',
          headerName: 'Value',
          columnGroupShow: 'open',
          enableRowGroup: false,
          ...defaultNumberDef('initial_vpv', 2, formattedCurrency, 'value'),
        },
        {
          field: 'initial_vpv.price',
          headerName: 'Price',
          columnGroupShow: 'open',
          enableRowGroup: false,
          ...defaultNumberDef('initial_vpv', 4, formattedCurrency, 'price'),
        },
      ],
    },
    {
      field: 'delivered_vpv',
      headerName: 'DELIVERED',
      groupId: 'delivered',
      children: [
        {
          field: 'delivered_vpv.volume',
          headerName: 'Volume',
          enableRowGroup: false,
          ...defaultNumberDef('delivered_vpv', 0, null, 'volume'),
          valueGetter: ({ data }) => data?.delivered_vpv?.volume,
        },
        {
          field: 'delivered_vpv.value',
          headerName: 'Value',
          columnGroupShow: 'open',
          enableRowGroup: false,
          ...defaultNumberDef('delivered_vpv', 2, formattedCurrency, 'value'),
        },
        {
          field: 'delivered_vpv.price',
          headerName: 'Price',
          columnGroupShow: 'open',
          enableRowGroup: false,
          ...defaultNumberDef('delivered_vpv', 4, formattedCurrency, 'price'),
        },
      ],
    },
    {
      field: 'sale_vpv',
      headerName: 'SOLD',
      groupId: 'sale',
      children: [
        {
          field: 'sale_vpv.volume',
          headerName: 'Volume',
          enableRowGroup: false,
          ...defaultNumberDef('sale_vpv', 0, null, 'volume'),
          valueGetter: ({ data }) => data?.sale_vpv?.volume,
        },
        {
          field: 'sale_vpv.value',
          headerName: 'Value',
          columnGroupShow: 'open',
          enableRowGroup: false,
          ...defaultNumberDef('sale_vpv', 2, formattedCurrency, 'value'),
        },
        {
          field: 'sale_vpv.price',
          headerName: 'Price',
          columnGroupShow: 'open',
          enableRowGroup: false,
          ...defaultNumberDef('sale_vpv', 4, formattedCurrency, 'price'),
        },
      ],
    },
    {
      field: 'final_vpv',
      headerName: 'FINAL',
      groupId: 'final',
      children: [
        {
          field: 'final_vpv.volume',
          headerName: 'Volume',
          enableRowGroup: false,
          ...defaultNumberDef('final_vpv', 0, null, 'volume'),
          valueGetter: ({ data }) => data?.final_vpv?.volume,
        },
        {
          field: 'final_vpv.value',
          headerName: 'Value',
          enableRowGroup: false,
          ...defaultNumberDef('final_vpv', 2, formattedCurrency, 'value'),
        },
        {
          field: 'final_vpv.price',
          headerName: 'Price',
          enableRowGroup: false,
          ...defaultNumberDef('final_vpv', 4, formattedCurrency, 'price'),
        },
      ],
    },
    {
      field: 'actual_sales',
      headerName: 'Actual Sales',
      enableRowGroup: false,
      hide: true,
      ...defaultNumberDef('actual_sales', 2, formattedCurrency, null),
    },
    {
      field: 'end_of_day_reading',
      headerName: 'Actual Inventory',
      enableRowGroup: false,
      hide: true,
      ...defaultNumberDef('end_of_day_reading', 0),
    },
  ]
}
